import { Button, Group, TextInput, useMantineColorScheme } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useInputState } from "@mantine/hooks";
import clsx from "clsx";
import React, { useEffect, useMemo, useState } from "react";
import { BiImages } from "react-icons/bi";
import { useSignUp } from "../hooks/auth-hook";
import { uploadFile } from "../utils"; // assumed to accept (setUrl, file)
import { PasswordStrength } from "./PasswordInput";

const MAX_MB = 5;
const ACCEPTED = ["image/jpeg", "image/png", "image/jpg"];

const SignUpForm = ({ toast, isSignin, setIsSignin, setFormClose, toggle }) => {
  const { colorScheme } = useMantineColorScheme();
  const theme = colorScheme === "dark";

  const { mutate } = useSignUp(toast, toggle);

  const [strength, setStrength] = useState(0);
  const [file, setFile] = useState(null);
  const [fileURL, setFileURL] = useState("");
  const [uploading, setUploading] = useState(false);
  const [passValue, setPassValue] = useInputState("");

  const form = useForm({
    initialValues: {
      email: "",
      firstName: "",
      lastName: "",
    },
    validate: {
      firstName: (v) => (v.trim().length < 3 ? "First name is too short" : null),
      lastName: (v) => (v.trim().length < 2 ? "Last name is too short" : null),
      email: (v) => (/^\S+@\S+$/.test(v.trim()) ? null : "Invalid email"),
    },
  });

  // handle file pick with validation
  const handlePick = (f) => {
    if (!f) return;
    if (!ACCEPTED.includes(f.type)) {
      toast.error("Only JPG and PNG images are allowed.");
      return;
    }
    const sizeMb = f.size / (1024 * 1024);
    if (sizeMb > MAX_MB) {
      toast.error(`Image must be ≤ ${MAX_MB} MB.`);
      return;
    }
    setFile(f);
  };

  // upload when file changes
  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    (async () => {
      try {
        setUploading(true);
        // your util takes (setUrl, file); if it returns a URL instead, adapt accordingly
        await uploadFile((url) => {
          if (!cancelled) setFileURL(url);
        }, file);
      } catch (e) {
        toast?.error("Failed to upload image. Try again.");
        setFileURL("");
      } finally {
        if (!cancelled) setUploading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [file, toast]);

  const handleSubmit = (values) => {
    if (!isSignin && strength < 90) {
      toast?.error("Please choose a stronger password.");
      return;
    }
    if (uploading) {
      toast?.message("Please wait for the image to finish uploading…");
      return;
    }

    const payload = {
      email: values.email.trim(),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      password: passValue,
      image: fileURL || undefined,
      accountType: "Writer",
    };

    // Close the form only after a successful signup
    mutate(payload, {
      onSuccess: () => {
        setFormClose?.(true);
      },
    });
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)} className="flex flex-col gap-3">
      <div className="w-full flex gap-2">
        <TextInput
          className="w-full"
          withAsterisk
          label="First Name"
          placeholder="First Name"
          {...form.getInputProps("firstName")}
        />
        <TextInput
          className="w-full"
          withAsterisk
          label="Last Name"
          placeholder="Last Name"
          {...form.getInputProps("lastName")}
        />
      </div>

      <TextInput
        withAsterisk
        label="Email Address"
        placeholder="your@email.com"
        {...form.getInputProps("email")}
      />

      <PasswordStrength
        value={passValue}
        setValue={setPassValue}
        setStrength={setStrength}
        isSignin={false}
      />

      <Group className="w-full flex justify-between" mt="md">
        <div className="flex items-center gap-2">
          <label
            className={clsx(
              "flex items-center gap-1 text-base cursor-pointer",
              theme ? "text-gray-500" : "text-slate-700"
            )}
            htmlFor="imgUpload"
          >
            <input
              type="file"
              onChange={(e) => handlePick(e.target.files?.[0] ?? null)}
              className="hidden"
              id="imgUpload"
              data-max-size="5120"
              accept=".jpg,.png,.jpeg"
            />
            <BiImages />
            <span>Picture</span>
          </label>
          {uploading && <span className="text-sm opacity-70">Uploading…</span>}
          {fileURL && <span className="text-sm text-green opacity-70">Image Ready ✓</span>}
        </div>

        <Button
          type="submit"
          loading={uploading}
          className={clsx(theme ? "bg-blue-600" : "bg-black")}
        >
          Submit
        </Button>
      </Group>

      <p className="text-sm">
        {isSignin ? "Don't have an account?" : "Already have an account?"}
        <span
          className="underline text-blue-600 ml-1 cursor-pointer"
          onClick={() => setIsSignin?.((prev) => !prev)}
        >
          {isSignin ? "Sign up" : "Sign in"}
        </span>
      </p>
    </form>
  );
};

export default SignUpForm;
